// client/src/pages/admin/ManageUnsubscribeTemplates.js
// Admin UI for managing unsubscribe footer templates.
// These are HTML snippets appended to eCard notification emails.
//
// Placeholders:
//   {{sender_name}}            — Name of the person sending the eCard
//   {{app_name}}               — Site name (e.g., "The Cherry Fairy Shop")
//   {{unsubscribe_sender_url}} — Unsubscribe from this sender only
//   {{unsubscribe_global_url}} — Unsubscribe from all eCards on platform
//   {{privacy_url}}            — Link to privacy policy

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';

export default function ManageUnsubscribeTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedPlaceholder, setCopiedPlaceholder] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    htmlcontent: '',
    isdefault: false,
    isactive: true,
  });

  const placeholders = [
    { key: '{{sender_name}}', description: 'Name of the eCard sender' },
    { key: '{{app_name}}', description: 'Site name (e.g., "The Cherry Fairy Shop")' },
    { key: '{{unsubscribe_sender_url}}', description: 'Link to unsubscribe from this sender only' },
    { key: '{{unsubscribe_global_url}}', description: 'Link to unsubscribe from all eCards' },
    { key: '{{privacy_url}}', description: 'Link to privacy policy' },
  ];

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/unsubscribe-templates');
      setTemplates(res.unsubscribeTemplates || []);
      setError(null);
    } catch (err) {
      setError('Failed to load unsubscribe templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      htmlcontent: '',
      isdefault: false,
      isactive: true,
    });
    setEditingTemplate(null);
    setShowForm(false);
    setPreviewHtml(null);
  };

  const handleEdit = (template) => {
    setForm({
      name: template.name || '',
      description: template.description || '',
      htmlcontent: template.htmlcontent || '',
      isdefault: template.isdefault || false,
      isactive: template.isactive !== false,
    });
    setEditingTemplate(template);
    setShowForm(true);
    setPreviewHtml(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.htmlcontent.trim()) {
      setError('Name and HTML content are required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/admin/unsubscribe-templates/${editingTemplate.templateid}`, form);
      } else {
        await api.post('/admin/unsubscribe-templates', form);
      }
      await fetchTemplates();
      resetForm();
      setError(null);
    } catch (err) {
      setError(err.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return;

    try {
      await api.del(`/admin/unsubscribe-templates/${template.templateid}`);
      await fetchTemplates();
      setError(null);
    } catch (err) {
      setError(err.data?.error || 'Failed to delete template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await api.patch(`/admin/unsubscribe-templates/${template.templateid}/set-default`);
      await fetchTemplates();
      setError(null);
    } catch (err) {
      setError(err.data?.error || 'Failed to set default');
    }
  };

  const handlePreview = () => {
    // Replace placeholders with sample data for preview
    const sampleHtml = form.htmlcontent
      .replace(/\{\{sender_name\}\}/g, 'John Smith')
      .replace(/\{\{app_name\}\}/g, 'The Cherry Fairy Shop')
      .replace(/\{\{unsubscribe_sender_url\}\}/g, '#unsubscribe-sender')
      .replace(/\{\{unsubscribe_global_url\}\}/g, '#unsubscribe-global')
      .replace(/\{\{privacy_url\}\}/g, '#privacy');
    setPreviewHtml(sampleHtml);
  };

  const copyPlaceholder = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedPlaceholder(key);
    setTimeout(() => setCopiedPlaceholder(null), 1500);
  };

  const insertPlaceholder = (key) => {
    const textarea = document.getElementById('htmlcontent-textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = form.htmlcontent.slice(0, start) + key + form.htmlcontent.slice(end);
      setForm({ ...form, htmlcontent: newContent });
      // Reset cursor position after React re-render
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + key.length, start + key.length);
      }, 0);
    } else {
      setForm({ ...form, htmlcontent: form.htmlcontent + key });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-gold">Unsubscribe Footer Templates</h1>
          <p className="text-royal-200 text-sm mt-1">
            HTML footers appended to eCard notification emails with unsubscribe links
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-royal-900 rounded-lg hover:bg-gold/90 transition-colors font-medium"
          >
            + New Template
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-400 flex-shrink-0">⚠️</span>
          <div className="flex-1">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            ✕
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-royal-800/50 border border-royal-600 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-display text-white">
              {editingTemplate ? 'Edit Template' : 'New Unsubscribe Template'}
            </h2>
            <button
              onClick={resetForm}
              className="text-royal-300 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Template Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Default Footer, Minimal Footer"
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-300 focus:outline-none focus:border-gold/50 transition-colors"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of when to use this template"
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-300 focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>

            {/* Placeholders Reference */}
            <div className="bg-royal-900/50 border border-royal-700 rounded-lg p-4">
              <p className="text-sm text-royal-200 mb-3">
                <strong className="text-white">Available Placeholders</strong> — Click to insert at cursor
              </p>
              <div className="flex flex-wrap gap-2">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => insertPlaceholder(p.key)}
                    onDoubleClick={() => copyPlaceholder(p.key)}
                    title={p.description + ' (double-click to copy)'}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-royal-800 border border-royal-600 rounded text-xs font-mono text-gold hover:bg-royal-700 hover:border-gold/30 transition-colors"
                  >
                    {p.key}
                    {copiedPlaceholder === p.key && (
                      <span className="text-green-400">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* HTML Content */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm text-royal-200">HTML Content *</label>
                <button
                  type="button"
                  onClick={handlePreview}
                  className="text-xs text-gold hover:text-gold/80 flex items-center gap-1"
                >
                  👁 Preview
                </button>
              </div>
              <textarea
                id="htmlcontent-textarea"
                value={form.htmlcontent}
                onChange={(e) => setForm({ ...form, htmlcontent: e.target.value })}
                rows={12}
                placeholder="<table>...</table>"
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-300 focus:outline-none focus:border-gold/50 transition-colors font-mono resize-y"
                required
              />
            </div>

            {/* Preview */}
            {previewHtml && (
              <div className="border border-royal-600 rounded-lg overflow-hidden">
                <div className="bg-royal-700/50 px-4 py-2 flex items-center justify-between">
                  <span className="text-sm text-royal-200">Preview (with sample data)</span>
                  <button
                    type="button"
                    onClick={() => setPreviewHtml(null)}
                    className="text-royal-300 hover:text-white"
                  >
                    ✕
                  </button>
                </div>
                <div
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            )}

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isdefault}
                  onChange={(e) => setForm({ ...form, isdefault: e.target.checked })}
                  className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/30"
                />
                <span className="text-sm text-royal-200">Set as default</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isactive}
                  onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                  className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/30"
                />
                <span className="text-sm text-royal-200">Active</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 bg-gold text-royal-900 rounded-lg hover:bg-gold/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 bg-royal-700 text-white rounded-lg hover:bg-royal-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      {!showForm && templates.length === 0 ? (
        <div className="bg-royal-800/30 border border-royal-700 rounded-xl p-12 text-center">
          <p className="text-royal-300">No unsubscribe templates yet.</p>
          <p className="text-royal-400 text-sm mt-1">
            Create one to add professional unsubscribe footers to your eCard emails.
          </p>
        </div>
      ) : (
        !showForm && (
          <div className="space-y-3">
            {templates.map((template) => (
              <div
                key={template.templateid}
                className="bg-royal-800/50 border border-royal-600 rounded-xl p-5 hover:border-royal-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-medium truncate">{template.name}</h3>
                      {template.isdefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full">
                          ⭐ Default
                        </span>
                      )}
                      {!template.isactive && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-300 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-royal-300 text-sm mb-2">{template.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-royal-400">
                      <span>
                        Used by {template.usage_count || 0} notification template{template.usage_count !== 1 ? 's' : ''}
                      </span>
                      <span>
                        Updated {new Date(template.updatedat || template.createdat).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!template.isdefault && template.isactive && (
                      <button
                        onClick={() => handleSetDefault(template)}
                        title="Set as default"
                        className="p-2 text-royal-300 hover:text-gold hover:bg-royal-700 rounded-lg transition-colors text-sm"
                      >
                        ⭐
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(template)}
                      title="Edit"
                      className="p-2 text-royal-300 hover:text-white hover:bg-royal-700 rounded-lg transition-colors text-sm"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      title="Delete"
                      className="p-2 text-royal-300 hover:text-red-400 hover:bg-royal-700 rounded-lg transition-colors text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* HTML Preview Snippet */}
                <div className="mt-3 pt-3 border-t border-royal-700">
                  <pre className="text-xs text-royal-400 font-mono overflow-x-auto max-h-20 whitespace-pre-wrap">
                    {template.htmlcontent?.slice(0, 300)}
                    {template.htmlcontent?.length > 300 && '...'}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Help Text */}
      {!showForm && templates.length > 0 && (
        <div className="bg-royal-800/30 border border-royal-700 rounded-lg p-4 text-sm text-royal-300">
          <p>
            <strong className="text-white">How it works:</strong> When sending an eCard, the system
            fetches the unsubscribe template assigned to the notification email template (or the default
            if none specified), replaces the placeholders with real URLs, and appends it to the email HTML.
          </p>
        </div>
      )}
    </div>
  );
}